import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Grid,
  Modal,
  Progress,
  Spin,
  Steps,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  MessageOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useCandidateSummaryFlow } from '../hooks/useCandidateSummaryFlow';
import {
  ASSESSMENT_SECTIONS,
  CANDIDATE_ASSESSMENT_DISCLAIMER,
  PRESENTATION_BRIEF_WORKFLOW,
  TABLE_COLUMNS,
  calculateAiBriefFitScore,
  interviewRecommendationGuidance,
  interviewRecommendationLabel,
  niceToHaveStatusColor,
  niceToHaveStatusLabel,
  recommendationClientGuidance,
  recommendationLabel,
  recommendationTagColor,
  skillSourceLabel,
  skillStatusColor,
  skillStatusLabel,
} from '../utils/candidate-summary-labels';
import {
  CandidateSummaryNiceToHaveAssessment,
  CandidateSummaryNiceToHaveStatus,
  CandidateSummaryPayload,
  CandidateSummaryRecommendation,
  CandidateSummaryRequiredSkillAssessment,
  CandidateSummarySkillSource,
  CandidateSummarySkillStatus,
} from '../models/candidate-summary.model';
import { CandidateSummaryService } from '../services/candidate-summary.service';
import { CandidateSummaryEditForm } from './CandidateSummaryEditForm';
import { CandidateResumeAiReview } from './CandidateResumeAiReview';
import { CandidateSummaryGapsSection } from './CandidateSummaryGapsSection';
import { CandidateSummaryGapHistoryPanel } from './CandidateSummaryGapHistoryPanel';
import {
  countOpenGaps,
  hasOpenGaps,
  normalizeAssessmentGaps,
  validateGapsForSave,
  withNormalizedGaps,
} from '../utils/candidate-summary-gaps';
import './CandidateSummaryPanel.css';

const { useBreakpoint } = Grid;
const { Paragraph, Text, Title } = Typography;

interface CandidateSummaryPanelProps {
  applicationId: string;
  resumeProcessing?: boolean;
  candidateName?: string;
  jobTitle?: string;
  companyName?: string;
  embeddedInCollapse?: boolean;
}

const recommendationHeroClass: Record<CandidateSummaryRecommendation, string> =
  {
    STRONG_MATCH: 'ai-brief-recommendation--strong',
    PARTIAL_MATCH: 'ai-brief-recommendation--partial',
    WEAK_MATCH: 'ai-brief-recommendation--weak',
  };

const sectionIntroClass = 'ai-brief-section__intro';

const sectionTitleClass = 'ai-brief-section__title';

const tableClassName = 'ai-brief-table';

type BriefSection = 'summary' | 'skills' | 'gaps' | 'interview';

function formatAuditTimestamp(value?: string): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function workflowStepIndex(
  phase: string,
  hasAssessment: boolean,
  openGapsRemain: boolean,
  isEditing: boolean
): number {
  if (
    !hasAssessment ||
    phase === 'idle' ||
    phase === 'generating' ||
    phase === 'loading'
  ) {
    return 0;
  }
  if (isEditing || openGapsRemain) {
    return 1;
  }
  return 2;
}

function workflowStatusLabel(
  phase: string,
  hasAssessment: boolean,
  openGapsRemain: boolean,
  isEditing: boolean
): { text: string; color: string } | null {
  if (phase === 'generating' || phase === 'loading') {
    return {
      text: PRESENTATION_BRIEF_WORKFLOW.status.generating,
      color: 'processing',
    };
  }
  if (!hasAssessment) {
    return {
      text: PRESENTATION_BRIEF_WORKFLOW.status.notStarted,
      color: 'default',
    };
  }
  if (isEditing) {
    return {
      text: PRESENTATION_BRIEF_WORKFLOW.status.editing,
      color: 'processing',
    };
  }
  if (openGapsRemain) {
    return {
      text: PRESENTATION_BRIEF_WORKFLOW.status.needsReview,
      color: 'warning',
    };
  }
  return {
    text: PRESENTATION_BRIEF_WORKFLOW.status.readyToExport,
    color: 'success',
  };
}

function InsightList({
  title,
  items,
  emptyLabel,
}: {
  title: string;
  items?: string[];
  emptyLabel: string;
}) {
  return (
    <div className="ai-brief-insight-card">
      <h4>{title}</h4>
      {items?.length ? (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="ai-brief-empty-copy">{emptyLabel}</p>
      )}
    </div>
  );
}

function RequiredSkillCard({
  item,
}: {
  item: CandidateSummaryRequiredSkillAssessment;
}) {
  return (
    <article className="ai-brief-skill-card">
      <Text strong className="ai-brief-skill-card__title">
        {item.skill}
      </Text>
      <div>
        <Text type="secondary" className="text-xs block mb-1">
          {TABLE_COLUMNS.resumeFit}
        </Text>
        <Tag
          color={skillStatusColor[item.status]}
          className="rounded-md m-0 w-fit whitespace-normal"
        >
          {skillStatusLabel[item.status]}
        </Tag>
      </div>
      <div>
        <Text type="secondary" className="text-xs block mb-1">
          {TABLE_COLUMNS.skillSource}
        </Text>
        <Tag className="rounded-md m-0 max-w-full whitespace-normal h-auto py-0.5">
          {skillSourceLabel[item.source]}
        </Tag>
      </div>
      <div>
        <Text type="secondary" className="text-xs block mb-1">
          {TABLE_COLUMNS.resumeEvidence}
        </Text>
        <p className="ai-brief-skill-card__evidence">{item.evidence}</p>
      </div>
    </article>
  );
}

function NiceToHaveSkillCard({
  item,
}: {
  item: CandidateSummaryNiceToHaveAssessment;
}) {
  return (
    <article className="ai-brief-skill-card">
      <Text strong className="ai-brief-skill-card__title">
        {item.skill}
      </Text>
      <div>
        <Text type="secondary" className="text-xs block mb-1">
          {TABLE_COLUMNS.resumeFit}
        </Text>
        <Tag
          color={niceToHaveStatusColor[item.status]}
          className="rounded-md m-0 w-fit whitespace-normal"
        >
          {niceToHaveStatusLabel[item.status]}
        </Tag>
      </div>
      <div>
        <Text type="secondary" className="text-xs block mb-1">
          {TABLE_COLUMNS.resumeEvidence}
        </Text>
        <p className="ai-brief-skill-card__evidence">{item.evidence}</p>
      </div>
    </article>
  );
}

export const CandidateSummaryPanel: React.FC<CandidateSummaryPanelProps> = ({
  applicationId,
  resumeProcessing = false,
  candidateName,
  jobTitle,
  companyName,
  embeddedInCollapse = false,
}) => {
  const screens = useBreakpoint();
  const isCompactLayout = !screens.lg;

  const { phase, summary, progressMessage, errorMessage, generate, reload } =
    useCandidateSummaryFlow(applicationId, true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draftPayload, setDraftPayload] =
    useState<CandidateSummaryPayload | null>(null);
  const [activeSection, setActiveSection] = useState<BriefSection>('summary');

  useEffect(() => {
    if (phase !== 'completed') {
      setIsEditing(false);
      setDraftPayload(null);
    }
  }, [phase]);

  const payload = summary?.payload;
  const isGenerating = phase === 'generating' || phase === 'loading';
  const hasAssessment = Boolean(payload && phase === 'completed');
  const displayPayload = isEditing && draftPayload ? draftPayload : payload;
  const openGapsRemain = hasOpenGaps(displayPayload);
  const actionButtonClass = isCompactLayout ? 'w-full' : '';
  const workflowStep = workflowStepIndex(
    phase,
    hasAssessment,
    openGapsRemain,
    isEditing
  );
  const statusBadge = workflowStatusLabel(
    phase,
    hasAssessment,
    openGapsRemain,
    isEditing
  );
  const requiredSkills = payload?.requiredSkillsAssessment ?? [];
  const requiredSkillsWithEvidence = requiredSkills.filter(
    (skill) => skill.status === 'MET'
  ).length;
  const requiredSkillCoverage = calculateAiBriefFitScore(requiredSkills);
  const openGapCount = countOpenGaps(displayPayload);
  const totalGapCount = displayPayload
    ? normalizeAssessmentGaps(displayPayload).length
    : 0;
  const interviewTalkingPointCount = payload?.interviewFocus?.length ?? 0;

  const requiredSkillColumns: ColumnsType<CandidateSummaryRequiredSkillAssessment> =
    [
      {
        title: TABLE_COLUMNS.mustHaveSkill,
        dataIndex: 'skill',
        key: 'skill',
        width: 160,
        align: 'left',
        render: (skill: string) => (
          <Text strong className="text-left">
            {skill}
          </Text>
        ),
      },
      {
        title: TABLE_COLUMNS.skillSource,
        dataIndex: 'source',
        key: 'source',
        width: 188,
        align: 'left',
        render: (source: CandidateSummarySkillSource) => (
          <Tag className="rounded-md m-0 whitespace-normal">
            {skillSourceLabel[source]}
          </Tag>
        ),
      },
      {
        title: TABLE_COLUMNS.resumeFit,
        dataIndex: 'status',
        key: 'status',
        width: 148,
        align: 'left',
        render: (status: CandidateSummarySkillStatus) => (
          <Tag
            color={skillStatusColor[status]}
            className="rounded-md m-0 whitespace-normal"
          >
            {skillStatusLabel[status]}
          </Tag>
        ),
      },
      {
        title: TABLE_COLUMNS.resumeEvidence,
        dataIndex: 'evidence',
        key: 'evidence',
        align: 'left',
        render: (evidence: string) => (
          <Text className="text-gray-700 text-left">{evidence}</Text>
        ),
      },
    ];

  const niceToHaveColumns: ColumnsType<CandidateSummaryNiceToHaveAssessment> = [
    {
      title: TABLE_COLUMNS.preferredSkill,
      dataIndex: 'skill',
      key: 'skill',
      width: 180,
      align: 'left',
      render: (skill: string) => (
        <Text strong className="text-left">
          {skill}
        </Text>
      ),
    },
    {
      title: TABLE_COLUMNS.resumeFit,
      dataIndex: 'status',
      key: 'status',
      width: 148,
      align: 'left',
      render: (status: CandidateSummaryNiceToHaveStatus) => (
        <Tag
          color={niceToHaveStatusColor[status]}
          className="rounded-md m-0 whitespace-normal"
        >
          {niceToHaveStatusLabel[status]}
        </Tag>
      ),
    },
    {
      title: TABLE_COLUMNS.resumeEvidence,
      dataIndex: 'evidence',
      key: 'evidence',
      align: 'left',
      render: (evidence: string) => (
        <Text className="text-gray-700 text-left">{evidence}</Text>
      ),
    },
  ];

  const handleDownloadPdf = async () => {
    if (hasOpenGaps(payload)) {
      message.warning(PRESENTATION_BRIEF_WORKFLOW.exportBlockedHint);
      return;
    }
    setDownloadingPdf(true);
    try {
      const blob = await CandidateSummaryService.DownloadPdf(
        applicationId,
        undefined,
        false
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `candidate-presentation-brief-${applicationId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      message.success('PDF downloaded.');
    } catch {
      message.error('Could not download PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleGenerate = () => {
    if (isEditing) {
      return;
    }
    if (payload && phase === 'completed') {
      Modal.confirm({
        title: 'Regenerate Assessment?',
        content:
          'This replaces the current brief with a newly generated assessment. Unsaved edits will be lost.',
        okText: 'Regenerate',
        cancelText: 'Cancel',
        onOk: () => generate(),
      });
      return;
    }
    void generate();
  };

  const handleStartEdit = () => {
    if (!payload) {
      return;
    }
    setDraftPayload(withNormalizedGaps(payload));
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftPayload(null);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (
      !draftPayload?.headline?.trim() ||
      !draftPayload.executiveSummary?.trim()
    ) {
      message.error('Headline and recruiter summary are required.');
      return;
    }
    const gapValidationError = validateGapsForSave(draftPayload.gaps ?? []);
    if (gapValidationError) {
      message.error(gapValidationError);
      return;
    }
    const payloadToSave = withNormalizedGaps(draftPayload);
    setIsSaving(true);
    try {
      const updated = await CandidateSummaryService.UpdateSummary(
        applicationId,
        payloadToSave
      );
      setIsEditing(false);
      setDraftPayload(null);
      await reload();
      if (updated?.payload) {
        message.success('Presentation Brief saved.');
      }
    } catch {
      message.error('Could not save Presentation Brief.');
    } finally {
      setIsSaving(false);
    }
  };

  const sectionWrapperClass = embeddedInCollapse
    ? 'text-left'
    : 'mt-6 sm:mt-8 border-t border-gray-200 pt-4 sm:pt-6 text-left';

  return (
    <section
      aria-labelledby="candidate-summary-heading"
      className={sectionWrapperClass}
    >
      <div className="ai-brief-shell">
        <header className="ai-brief-header">
          <div className="ai-brief-header__identity">
            <span className="ai-brief-header__icon">
              <RobotOutlined aria-hidden />
            </span>
            <div className="ai-brief-header__copy">
              <div className="ai-brief-header__title-row">
                <Title level={5} id="candidate-summary-heading">
                  AI Brief
                </Title>
                {statusBadge ? (
                  <Tag color={statusBadge.color} className="ai-brief-status">
                    {statusBadge.text}
                  </Tag>
                ) : null}
              </div>
              <Text type="secondary">
                A recruiter-ready assessment built from the job requirements and
                resume.
              </Text>
              {(candidateName || jobTitle || companyName) && (
                <p className="ai-brief-header__context">
                  {[candidateName, jobTitle, companyName]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
          </div>

          <div className="ai-brief-actions">
            {hasAssessment && !isEditing && (
              <>
                <Tooltip
                  title={
                    isCompactLayout
                      ? undefined
                      : 'Edit recommendation, summary, and gaps'
                  }
                >
                  <Button
                    className={actionButtonClass}
                    icon={<EditOutlined />}
                    onClick={handleStartEdit}
                    disabled={isGenerating}
                  >
                    Edit Brief
                  </Button>
                </Tooltip>
                <Tooltip
                  title={
                    openGapsRemain
                      ? PRESENTATION_BRIEF_WORKFLOW.exportBlockedHint
                      : 'Download a client-ready PDF'
                  }
                >
                  <span className={isCompactLayout ? 'w-full' : ''}>
                    <Button
                      type={openGapsRemain ? 'default' : 'primary'}
                      className={isCompactLayout ? 'w-full' : ''}
                      icon={<DownloadOutlined />}
                      loading={downloadingPdf}
                      onClick={() => void handleDownloadPdf()}
                      disabled={openGapsRemain}
                    >
                      Export PDF
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            {isEditing && (
              <>
                <Button
                  className={actionButtonClass}
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  className={actionButtonClass}
                  loading={isSaving}
                  onClick={() => void handleSaveEdit()}
                >
                  Save Changes
                </Button>
              </>
            )}
            {!isEditing && (
              <Button
                type={hasAssessment ? 'default' : 'primary'}
                className={actionButtonClass}
                icon={hasAssessment ? <ReloadOutlined /> : <RobotOutlined />}
                loading={phase === 'generating'}
                onClick={handleGenerate}
                disabled={isGenerating || resumeProcessing}
              >
                {hasAssessment ? 'Regenerate' : 'Generate Assessment'}
              </Button>
            )}
          </div>
        </header>

        {!hasAssessment && (
          <div className="ai-brief-workflow">
            <Steps
              size="small"
              current={workflowStep}
              responsive={false}
              items={PRESENTATION_BRIEF_WORKFLOW.steps.map((step) => ({
                title: step.title,
                description: isCompactLayout ? undefined : step.description,
              }))}
            />
          </div>
        )}

        <div className="ai-brief-body">
          {resumeProcessing && (
            <Alert
              className="ai-brief-alert"
              type="warning"
              showIcon
              message="Resume still processing"
              description="The assessment may be incomplete until resume parsing finishes."
            />
          )}

          {phase === 'loading' && (
            <div className="ai-brief-state" role="status" aria-live="polite">
              <Spin />
              <div>
                <Text strong>Loading AI Brief</Text>
                <Text type="secondary">Retrieving the latest assessment…</Text>
              </div>
            </div>
          )}

          {phase === 'generating' && (
            <div
              className="ai-brief-state ai-brief-state--processing"
              role="status"
              aria-live="polite"
            >
              <Spin />
              <div>
                <Text strong>Generating AI Brief</Text>
                <Text type="secondary">
                  {progressMessage ||
                    'This usually takes under a minute. You can stay on this tab.'}
                </Text>
              </div>
            </div>
          )}

          {phase === 'failed' && (
            <Alert
              className="ai-brief-alert"
              type="error"
              showIcon
              message="AI Brief unavailable"
              description={errorMessage ?? 'Assessment failed.'}
              action={
                <Button size="small" onClick={() => void reload()}>
                  Retry
                </Button>
              }
            />
          )}

          {hasAssessment && displayPayload && payload && (
            <div className="ai-brief-content">
              {!isEditing && (
                <section
                  className={`ai-brief-interview-decision ${
                    recommendationHeroClass[displayPayload.recommendation]
                  }`}
                  aria-label="Interview recommendation"
                >
                  <span className="ai-brief-interview-decision__icon">
                    {displayPayload.recommendation === 'STRONG_MATCH' ? (
                      <CheckCircleOutlined aria-hidden />
                    ) : (
                      <ExclamationCircleOutlined aria-hidden />
                    )}
                  </span>
                  <div className="ai-brief-interview-decision__copy">
                    <span>Interview recommendation</span>
                    <strong>
                      {
                        interviewRecommendationLabel[
                          displayPayload.recommendation
                        ]
                      }
                    </strong>
                    <p>
                      {
                        interviewRecommendationGuidance[
                          displayPayload.recommendation
                        ]
                      }
                    </p>
                  </div>
                  {openGapsRemain && (
                    <Button
                      size="small"
                      onClick={() => setActiveSection('gaps')}
                    >
                      Review {openGapCount} gap
                      {openGapCount === 1 ? '' : 's'}
                    </Button>
                  )}
                </section>
              )}

              <div
                className="ai-brief-metrics"
                aria-label="Assessment overview"
              >
                <article className="ai-brief-metric">
                  <span className="ai-brief-metric__icon ai-brief-metric__icon--blue">
                    <SafetyCertificateOutlined aria-hidden />
                  </span>
                  <div className="ai-brief-metric__content">
                    <span>AI Brief fit score</span>
                    <strong>{requiredSkillCoverage}%</strong>
                    <Progress
                      percent={requiredSkillCoverage}
                      showInfo={false}
                      size="small"
                      strokeColor="var(--jo-primary)"
                    />
                    <small>
                      {requiredSkillsWithEvidence}/{requiredSkills.length} fully
                      evidenced
                    </small>
                  </div>
                </article>
                <article className="ai-brief-metric">
                  <span className="ai-brief-metric__icon ai-brief-metric__icon--amber">
                    <FileSearchOutlined aria-hidden />
                  </span>
                  <div className="ai-brief-metric__content">
                    <span>Gaps requiring review</span>
                    <strong>{openGapCount}</strong>
                    <small>{totalGapCount} total flagged</small>
                  </div>
                </article>
                <article className="ai-brief-metric">
                  <span className="ai-brief-metric__icon ai-brief-metric__icon--violet">
                    <MessageOutlined aria-hidden />
                  </span>
                  <div className="ai-brief-metric__content">
                    <span>Interview talking points</span>
                    <strong>{interviewTalkingPointCount}</strong>
                    <small>Prepared for recruiter review</small>
                  </div>
                </article>
              </div>

              {isEditing && draftPayload ? (
                <section className="ai-brief-editor" aria-label="Edit AI Brief">
                  <div className="ai-brief-section-heading">
                    <div>
                      <span className="ai-brief-eyebrow">Edit mode</span>
                      <h3>Refine the client-ready brief</h3>
                      <p>
                        Update the recommendation, narrative, interview
                        guidance, and gap resolutions. Skills evidence remains
                        read-only.
                      </p>
                    </div>
                  </div>
                  <CandidateSummaryEditForm
                    payload={draftPayload}
                    onChange={setDraftPayload}
                  />
                </section>
              ) : (
                <>
                  <Tabs
                    className="ai-brief-tabs"
                    activeKey={activeSection}
                    onChange={(key) => setActiveSection(key as BriefSection)}
                    items={[
                      {
                        key: 'summary',
                        label: 'Summary',
                        children: (
                          <div className="ai-brief-tab-panel">
                            <div
                              className={`ai-brief-recommendation ${
                                recommendationHeroClass[
                                  displayPayload.recommendation
                                ]
                              }`}
                            >
                              <div className="ai-brief-recommendation__status">
                                <Tag
                                  color={
                                    recommendationTagColor[
                                      displayPayload.recommendation
                                    ]
                                  }
                                >
                                  {
                                    recommendationLabel[
                                      displayPayload.recommendation
                                    ]
                                  }
                                </Tag>
                                <span>
                                  {
                                    recommendationClientGuidance[
                                      displayPayload.recommendation
                                    ]
                                  }
                                </span>
                              </div>
                              <h4>{displayPayload.headline}</h4>
                              <Paragraph>
                                {displayPayload.executiveSummary}
                              </Paragraph>
                            </div>
                            <CandidateResumeAiReview
                              review={payload.resumeAiAssessment}
                            />
                            <div className="ai-brief-summary-grid">
                              <InsightList
                                title={ASSESSMENT_SECTIONS.keyStrengths}
                                items={payload.strengths}
                                emptyLabel="No strengths noted."
                              />
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: 'skills',
                        label: (
                          <span className="ai-brief-tab-label">
                            Skills evidence
                            <span>{requiredSkills.length}</span>
                          </span>
                        ),
                        children: (
                          <div className="ai-brief-tab-panel ai-brief-tab-panel--stacked">
                            <section>
                              <div className="ai-brief-section-heading">
                                <div>
                                  <span className="ai-brief-eyebrow">
                                    Role requirements
                                  </span>
                                  <h3 className={sectionTitleClass}>
                                    {ASSESSMENT_SECTIONS.mustHaveSkills}
                                  </h3>
                                  <p className={sectionIntroClass}>
                                    {ASSESSMENT_SECTIONS.mustHaveSkillsIntro}
                                  </p>
                                </div>
                                <span className="ai-brief-evidence-score">
                                  {requiredSkillCoverage}% evidenced
                                </span>
                              </div>
                              {isCompactLayout ? (
                                <div className="ai-brief-card-list">
                                  {requiredSkills.map((item) => (
                                    <RequiredSkillCard
                                      key={`${item.skill}-${item.source}`}
                                      item={item}
                                    />
                                  ))}
                                  {requiredSkills.length === 0 && (
                                    <p className="ai-brief-empty-copy">
                                      No must-have skills assessed.
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="ai-brief-table-wrap">
                                  <Table
                                    className={tableClassName}
                                    size="middle"
                                    pagination={false}
                                    rowKey={(row) =>
                                      `${row.skill}-${row.source}`
                                    }
                                    dataSource={requiredSkills}
                                    columns={requiredSkillColumns}
                                    scroll={{ x: 900 }}
                                    locale={{
                                      emptyText:
                                        'No must-have skills assessed.',
                                    }}
                                  />
                                </div>
                              )}
                            </section>

                            {(payload.niceToHaveAssessment?.length ?? 0) >
                              0 && (
                              <section>
                                <div className="ai-brief-section-heading">
                                  <div>
                                    <span className="ai-brief-eyebrow">
                                      Additional value
                                    </span>
                                    <h3 className={sectionTitleClass}>
                                      {ASSESSMENT_SECTIONS.preferredSkills}
                                    </h3>
                                    <p className={sectionIntroClass}>
                                      {ASSESSMENT_SECTIONS.preferredSkillsIntro}
                                    </p>
                                  </div>
                                </div>
                                {isCompactLayout ? (
                                  <div className="ai-brief-card-list">
                                    {payload.niceToHaveAssessment.map(
                                      (item) => (
                                        <NiceToHaveSkillCard
                                          key={item.skill}
                                          item={item}
                                        />
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <div className="ai-brief-table-wrap">
                                    <Table
                                      className={tableClassName}
                                      size="middle"
                                      pagination={false}
                                      rowKey="skill"
                                      dataSource={payload.niceToHaveAssessment}
                                      columns={niceToHaveColumns}
                                      scroll={{ x: 760 }}
                                    />
                                  </div>
                                )}
                              </section>
                            )}
                          </div>
                        ),
                      },
                      {
                        key: 'gaps',
                        label: (
                          <span className="ai-brief-tab-label">
                            Gaps & risks
                            <span
                              className={openGapCount > 0 ? 'is-warning' : ''}
                            >
                              {openGapCount}
                            </span>
                          </span>
                        ),
                        children: (
                          <div className="ai-brief-tab-panel ai-brief-tab-panel--stacked">
                            <CandidateSummaryGapsSection
                              payload={displayPayload}
                            />
                            <CandidateSummaryGapHistoryPanel
                              history={summary?.gapResolutionHistory}
                            />
                          </div>
                        ),
                      },
                      {
                        key: 'interview',
                        label: (
                          <span className="ai-brief-tab-label">
                            Interview prep
                            <span>{interviewTalkingPointCount}</span>
                          </span>
                        ),
                        children: (
                          <div className="ai-brief-tab-panel">
                            <div className="ai-brief-section-heading">
                              <div>
                                <span className="ai-brief-eyebrow">
                                  Recruiter guidance
                                </span>
                                <h3>
                                  {ASSESSMENT_SECTIONS.interviewTalkingPoints}
                                </h3>
                                <p>
                                  Use these prompts to validate unclear evidence
                                  and explore the candidate&apos;s strongest
                                  fit.
                                </p>
                              </div>
                            </div>
                            <InsightList
                              title="Suggested talking points"
                              items={payload.interviewFocus}
                              emptyLabel="No interview talking points noted."
                            />
                          </div>
                        ),
                      },
                    ]}
                  />
                </>
              )}

              <footer className="ai-brief-audit">
                <Text type="secondary">
                  <ExclamationCircleOutlined aria-hidden />
                  {CANDIDATE_ASSESSMENT_DISCLAIMER}
                </Text>
                {(summary?.generatedBy || summary?.updatedAt) && (
                  <div>
                    {summary.generatedBy && (
                      <span>{`Prepared by ${summary.generatedBy}`}</span>
                    )}
                    {summary.updatedAt && (
                      <span>{`Last updated ${formatAuditTimestamp(
                        summary.updatedAt
                      )}`}</span>
                    )}
                  </div>
                )}
              </footer>
            </div>
          )}

          {phase === 'idle' && (
            <div className="ai-brief-empty">
              <span className="ai-brief-empty__icon">
                <RobotOutlined aria-hidden />
              </span>
              <div>
                <span className="ai-brief-eyebrow">AI-assisted assessment</span>
                <Title level={5}>
                  {PRESENTATION_BRIEF_WORKFLOW.emptyTitle}
                </Title>
                <Paragraph type="secondary">
                  {PRESENTATION_BRIEF_WORKFLOW.emptyIntro}
                </Paragraph>
                <div className="ai-brief-empty__features">
                  <span>
                    <SafetyCertificateOutlined aria-hidden />
                    Match required skills to resume evidence
                  </span>
                  <span>
                    <FileSearchOutlined aria-hidden />
                    Flag gaps that need recruiter review
                  </span>
                  <span>
                    <MessageOutlined aria-hidden />
                    Prepare interview talking points
                  </span>
                </div>
                <Button
                  type="primary"
                  size="large"
                  icon={<RobotOutlined />}
                  className={isCompactLayout ? 'w-full' : ''}
                  onClick={handleGenerate}
                  disabled={resumeProcessing}
                >
                  Generate AI Brief
                </Button>
                {resumeProcessing ? (
                  <Text type="secondary" className="ai-brief-empty__hint">
                    Wait until resume processing finishes before generating.
                  </Text>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
